import React, { useState, useEffect, useRef, useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';
import BasicButton from '../../components/BasicButton';
import BasicSection from '../../components/BasicSection';
import InputFileTable from '../../components/InputFileTable';
import LoadMask from '../../components/LoadMask';
import PanelContainer from '../../components/PanelContainer';
import {ANIMATION_TIMEOUT_APPEAR} from '../../constants/animation';
import ENDPOINT from '../../constants/endpoints';
import PERMISSIONS from '../../constants/permissions';
import ROUTE from '../../constants/route';
import USER_OPTIONS from '../../constants/user-options';
import useApi from '../../hooks/useApi';
import PageLayout from '../../layouts/PageLayout';
import CandidateProfile from '../../scenes/CandidateProfile';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import ConfirmDialog from '../../scenes/Dialog/components/ConfirmDialog';
import ResponseErrorDialog from '../../scenes/Dialog/components/ResponseErrorDialog';
import Sidebar from '../../scenes/Sidebar';
import InBounds from '../../scenes/Sidebar/components/InBounds';
import {FileValidationSchema} from './validate';
import './styles.css';

const Aptitude = props => {
  const {
    user,
    history,
    location,
    globalActions: {
      updateUserInboundsCount,
      updateUserPermission,
      updateUserOption,
    },
  } = props;

  const [isFetching, setFetching] = useState(false);
  const [state, setState] = useState({});
  const input = useRef(null);
  const api = useApi();

  useEffect(() => {
    const fileSelector = document.createElement('input');
    fileSelector.setAttribute('type', 'file');
    fileSelector.setAttribute(
      'accept',
      [
        '.doc',
        '.docx',
        '.pdf',
        '.ppt',
        '.pptx',
        '.zip',
        '.rar',
        '.gz',
        '.xls',
        '.xlsx',
        '.odt',
        '.rtf',
      ].join(',')
    );
    input.current = fileSelector;

    if (location.state && location.state.testCompleted) {
      BasicDialog({
        bodyRender:
          'Congratulations, you have completed the GoBuyside Aptitude Test.',
      });
      updateUserOption(USER_OPTIONS.HAS_COMPLIED_GAT, true);
      updateUserPermission(PERMISSIONS.RUN_APTITUDE_TEST);
      history.replace(location.pathname, null);
    }

    api.get(
      ENDPOINT.OTHER.APTITUDE,
      {},
      {
        handleError: response => ResponseErrorDialog({ response }),
        handleSuccess: response => setState(response),
      }
    );
  }, []);

  const sendData = (params, callback) => {
    setFetching(true);
    api.post(ENDPOINT.ACTION.APTITUDE.FILES_SAVE, params, {
      handleError: response => ResponseErrorDialog({ response }),
      handleSuccess: () => {
        callback && typeof callback === 'function' && callback();
        BasicDialog({ bodyRender: <p>Changes saved</p> });
      },
      handleComplete: () => setFetching(false),
    });
  };

  const updateFileInFileList = (filed, file) => {
    setState(prevState => ({
      ...prevState,
      files: prevState.files.map(f =>
        f.FileID === file.FileID
          ? { ...f, [filed]: f[filed] === '1' ? '0' : '1' }
          : f
      ),
    }));
  };

  const handleSave = () => {
    const params = {
      delete: state.files
        .filter(file => file.Deleted === '1')
        .map(file => file.FileID),
      file: state.files
        .filter(file => file.Show === '1')
        .map(file => file.FileID),
    };

    if (!!params.delete.length) {
      ConfirmDialog({
        dismissWhenClose: true,
        bodyRender: <p>Are you sure you want to delete selected files?</p>,
      }).then(() => {
        sendData(params, () =>
          setState(prevState => ({
            ...prevState,
            files: prevState.files.filter(f => f.Deleted === '0' || !f.Deleted),
          }))
        );
      });
    } else {
      sendData(params);
    }
  };

  const handleBrowse = type => {
    input.current.value = '';
    input.current.onchange = ({ target }) => {
      handleSelectFile(target, type);
    };
    input.current.click();
  };

  const handleSelectFile = (target, type) => {
    const [file = null] = target.files;

    FileValidationSchema.validate(file)
      .then(() => {
        setFetching(true);
        api.post(
          ENDPOINT.ACTION.APTITUDE.FILE_UPLOAD,
          { type, [`File${type}`]: file },
          {
            handleError: response => ResponseErrorDialog({ response }),
            handleSuccess: file => {
              setState(prevSate => ({
                ...prevSate,
                files: [...prevSate.files, file],
              }));
            },
            handleComplete: () => setFetching(false),
          }
        );
      })
      .catch(err => {
        const [message = 'ERROR'] = err.errors;
        BasicDialog({ bodyRender: <p>{message}</p> });
      });
  };

  const handleChangeShowFile = file => {
    updateFileInFileList('Show', file);
  };

  const handleRemoveFile = file => {
    updateFileInFileList('Deleted', file);
  };

  const handleTest = () => {
    ConfirmDialog({
      popupStyles: {
        maxWidth: '642px',
        padding: '35px 35px 45px 35px',
      },
      bodyRender: (
        <p
          style={{
            textAlign: 'left',
          }}
        >
          This test is one of many data points firms use to base their interview
          decisions. It is not worth it to cheat and receiving aid on this
          examination or distributing our copyrighted material is in direct
          violation of our terms of use.
        </p>
      ),
      textSuccessBtn: 'Start test',
      textCancelBtn: 'No Thanks',
      dismissWhenClose: true,
    }).then(() =>
      history.push(ROUTE.CANDIDATE_APTITUDE_TEST, { confirmed: true })
    );
  };

  const gatTestPanel = useMemo(() => {
    const hasResult = state.results && !!state.results.length;

    return state.isAllowedRunAptitudeTest || hasResult ? (
      <PanelContainer styles={{ marginBottom: '10px', paddingTop: '35px' }}>
        {state.isAllowedRunAptitudeTest && (
          <BasicSection
            title="GoBuyside Aptitude Test"
            buttons={[
              {
                caption: 'Start aptitude test',
                handler: handleTest,
              },
            ]}
          />
        )}

        {hasResult && (
          <div className="aptitude-page__content aptitude-page__content--info">
            <div className="aptitude-page__text">
              Congratulations, you have completed the GoBuyside Aptitude Test.
            </div>
            {state.results.map((result, index) => (
              <div key={index}>
                <div className="aptitude-page__text">
                  <span className="aptitude-page__strong">Test date:</span>
                  {result.DatePass}
                </div>
                <div className="aptitude-page__text">
                  <span className="aptitude-page__strong">{result.TotalResult}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </PanelContainer>
    ) : null;
  }, [state]);

  return (
    <PageLayout
      pageClass="aptitude-page"
      pageTitle="Aptitude"
      mainContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <CandidateProfile>
            {gatTestPanel}

            <PanelContainer
              styles={{
                marginBottom: '10px',
                paddingTop: '35px',
                position: 'relative',
              }}
            >
              {isFetching && <LoadMask relative />}
              <BasicSection title="My Work">
                <div className="aptitude-page__content">
                  {state.files &&
                    state.types &&
                    state.types.map(type => (
                      <div
                        className="aptitude-page__block"
                        key={type.FileTypeID}
                      >
                        <div className="aptitude-page__row">
                          <h5 className="aptitude-page__subtitle">
                            {`Upload ${type.FileTypeName}`}
                          </h5>
                          <div className="aptitude-page__button">
                            <BasicButton
                              btnWidth={130}
                              btnHeight={35}
                              btnFontWeight={600}
                              btnText="Browse"
                              btnArrow={false}
                              btnBg="#fff"
                              btnHoverBg="#f40"
                              controlFunc={() => handleBrowse(type.FileTypeID)}
                            />
                          </div>
                        </div>

                        <InputFileTable
                          files={state.files.filter(
                            file => file.FileTypeID === type.FileTypeID
                          )}
                          onRemoveHandler={handleRemoveFile}
                          onChangeHandler={handleChangeShowFile}
                        />
                      </div>
                    ))}
                </div>
                <div className="aptitude-page__controls">
                  <div className="aptitude-page__button">
                    <BasicButton
                      btnWidth={200}
                      btnHeight={40}
                      btnFontWeight={600}
                      btnText="Save"
                      btnArrow={true}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={handleSave}
                    />
                  </div>
                </div>
              </BasicSection>
            </PanelContainer>
          </CandidateProfile>
        </CSSTransition>
      }
      sidebarContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <Sidebar>
            <InBounds
              inboundsCount={user.inbounds}
              onInboundsUpdate={updateUserInboundsCount}
            />
          </Sidebar>
        </CSSTransition>
      }
    />
  );
};

export default Aptitude;
